import React, { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { ClassNames } from "@emotion/react";
import styled from "@emotion/styled";
import { toast } from "sonner";
import { Flex, Text } from "styles/utility-components";

import AiChatPanel from "components/ai_chat/AiChatPanel";
import QuickPromptsDrawer from "components/ai_chat/quick_prompts/QuickPromptsDrawer";
import QuickPromptsMenu from "components/ai_chat/quick_prompts/QuickPromptsMenu";
import { AIQuestionForm, AiSource, SuggestedQuestion } from "components/ai_chat/types";
import CustomerDocumentSelector from "components/form_components/CustomerDocumentSelector";
import { TextArea } from "components/form_components/Inputs";
import Button from "components/ui/button/Button";
import Card from "components/ui/card/Card";
import EmptyState from "components/ui/empty_state/EmptyState";
import Popover from "components/ui/popover/Popover";
import QuickActionButton from "components/ui/quick_action_button/QuickActionButton";
import Tooltip from "components/ui/tooltip/Tooltip";
import { copyToClipboard } from "lib/clipboard";
import AiSources from "lib/core_components/ai/AiSources";
import Badge from "lib/core_components/Badge";
import { useAiQuickPrompts } from "lib/hooks/api/ai_quick_prompts/useAiQuickPrompts";
import { useAiToneOfVoices } from "lib/hooks/api/ai_tone_of_voice/useAiToneOfVoices";
import { AiContextProvider } from "lib/providers/AiContextProvider";
import { EventData, EventNames, useTracking } from "lib/tracking";
import { AiQuickPromptChatType, CustomerDocumentTypeFilter } from "lib/types/graphQLEnums";
import { UIcon } from "../../../lib/icons/untitled_ui/UIcon";
import TovDropdown from "./TovDropdown";

// Leaves the cursor after the inserted prompt, ready to add to it
function focusInputAtEnd({ setFocus }: UseFormReturn<AIQuestionForm>) {
  setFocus("userInputQuestion");
  const input = document.activeElement;
  if (input instanceof HTMLTextAreaElement) {
    input.setSelectionRange(input.value.length, input.value.length);
    input.scrollTop = input.scrollHeight;
  }
}

type Props = {
  resourceId: string;
  resourceName?: string;
  bidQuestion: string;
  onInsertAnswer: (answer: string) => void;
};

export default function AiAssistant({
  resourceId,
  resourceName,
  bidQuestion,
  onInsertAnswer,
}: Props) {
  const { logEvent } = useTracking();

  const [selectedTovId, setSelectedTovId] = useState<string | undefined>(undefined);

  const { data: quickPrompts, isError: quickPromptsFailed } = useAiQuickPrompts(
    AiQuickPromptChatType.BidResponse,
  );
  // The menus offer the team's quick prompts as the chat panel's suggested questions
  const suggestedQuestions: SuggestedQuestion[] = (quickPrompts?.prompts ?? []).map(
    ({ id, scope, name, prompt }) => ({ id, scope, title: name ?? undefined, prompt }),
  );
  const quickPromptsUnavailableReason = quickPromptsFailed
    ? "Quick prompts couldn't be loaded"
    : undefined;
  const [isEditingQuickPrompts, setIsEditingQuickPrompts] = useState(false);

  const { data: tovEntries } = useAiToneOfVoices();
  const entries = tovEntries ?? [];
  const defaultId = entries.find((e) => e.isDefault)?.id;
  const effectiveTovId = selectedTovId ?? defaultId;
  const toneOfVoiceLabel = entries.find((e) => e.id === effectiveTovId)?.label;

  const handleTovSelect = (newId: string) => {
    const effectiveCurrentId = selectedTovId ?? defaultId;
    if (newId === effectiveCurrentId) return;

    const fromLabel = entries.find((e) => e.id === effectiveCurrentId)?.label;
    const toLabel = entries.find((e) => e.id === newId)?.label;

    if (fromLabel && toLabel) {
      logEvent(EventNames.tovSelectionChanged, {
        "From tone of voice label": fromLabel,
        "To tone of voice label": toLabel,
        "Is default tone of voice": toLabel === "Default",
        "Context source": "Ask AI",
        "Resource Id": resourceId,
      });
    }

    setSelectedTovId(newId);
  };

  // Tells the defaults apart from the team's own prompts in "Quick prompt clicked"
  const quickPromptTracking = ({ id, scope }: SuggestedQuestion): EventData => ({
    "Quick prompt id": id,
    "Quick prompt type": scope,
  });

  const openQuickPromptsEditor = () => {
    setIsEditingQuickPrompts(true);
    logEvent(EventNames.quickPromptsEditClicked, {
      "Resource Id": resourceId,
      "Resource name": resourceName,
      "Context source": "Ask AI",
    });
  };

  const handleCopyToClipboard = async (answer: string, conversationId: string) => {
    const copied = await copyToClipboard({ plain: answer });
    if (copied) {
      logEvent(EventNames.copyToClipboardClicked, {
        "Conversation Id": conversationId,
        "Resource Id": resourceId,
        "Resource name": resourceName,
        "Context source": "Ask AI",
      });
      void toast.success("Answer copied");
    } else {
      void toast.error("Failed to copy");
    }
  };

  return (
    <AiAssistantContainer>
      <AiContextProvider
        context={{
          resourceId,
          resourceName: resourceName || "",
          resourceType: "BID_WRITING_QA",
        }}
      >
        <AiChatPanel
          defaultSelectedSources={[]}
          additionalContext={bidQuestion}
          toneOfVoiceId={effectiveTovId}
          toneOfVoiceLabel={toneOfVoiceLabel}
          onNewConversation={() => setSelectedTovId(undefined)}
          // Quick prompts go into the input to be edited, rather than being sent
          autoAnswerSuggestedPrompt={false}
          suggestedQuestionsLayout="vertical"
          uiText={{
            title: "",
            subtitle: "",
          }}
          showHistory
          historyPlacement="inline"
          inlineHistoryContainerPadding="24px 16px"
          showDisclaimer={false}
          userInitialsType="small"
          mainContainerPadding={"0 12px"}
          chatThreadContainerPadding={"0 12px"}
          renderActions={(answer, conversationId, sources) => {
            if (!answer) {
              return <></>;
            }
            return (
              <ActionsContainer>
                <SourcesContainer>
                  <AiSources sources={sources as AiSource[]} />
                </SourcesContainer>
                <Flex gap={4}>
                  <Button
                    buttonType="secondary"
                    icon="arrowCircleLeft"
                    onClick={() => onInsertAnswer(answer)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                    }}
                  >
                    Insert
                  </Button>
                  <Tooltip content="Copy to clipboard" aria-label="Copy to clipboard">
                    <Button
                      buttonType="tertiary"
                      iconProps={{ color: "sysTextBlack" }}
                      icon="copy01"
                      onClick={() => handleCopyToClipboard(answer, conversationId)}
                    />
                  </Tooltip>
                </Flex>
              </ActionsContainer>
            );
          }}
          renderInput={({
            methods,
            handleQuestionSubmit,
            isLoading,
            currentConversation,
            selectSuggestedQuestion,
          }) => {
            const { control, formState, watch } = methods;
            const selectedSources = watch("selectedSources");
            return (
              <Card>
                <Flex column gap={16}>
                  {currentConversation && (
                    <QuickPromptsMenu
                      side="top"
                      prompts={suggestedQuestions}
                      unavailableReason={quickPromptsUnavailableReason}
                      trigger={
                        <Button
                          icon="magicWand"
                          buttonType="secondary"
                          width="fit-content"
                          disabled={isLoading || !quickPrompts}
                        >
                          Quick prompts
                        </Button>
                      }
                      onSelectPrompt={(quickPrompt) =>
                        void selectSuggestedQuestion(
                          quickPrompt.prompt,
                          quickPromptTracking(quickPrompt),
                        )
                      }
                      focusInput={() => focusInputAtEnd(methods)}
                      onEditPrompts={openQuickPromptsEditor}
                    />
                  )}
                  <ClassNames>
                    {({ css }) => {
                      return (
                        <TextArea
                          autoSize={{ minRows: 3, maxRows: 8 }}
                          autoFocus
                          name="userInputQuestion"
                          label={""}
                          control={control}
                          className={css({
                            width: "100%",
                            borderRadius: 4,
                          })}
                          placeholder="Ask anything"
                          disabled={isLoading}
                          aria-label="Ask a question"
                          showErrors={false}
                          onPressEnter={(e) => {
                            if (e.shiftKey) {
                              // Allow newline when Shift + Enter is pressed
                              return;
                            }
                            // Prevent newline and trigger submission on Enter without Shift
                            e.preventDefault();
                            e.stopPropagation();
                            void handleQuestionSubmit()();
                          }}
                        />
                      );
                    }}
                  </ClassNames>
                  <Flex justifyContent="space-between" alignItems="center">
                    <Flex gap={8} alignItems="center">
                      <Popover
                        trigger={
                          <Button buttonType="secondary" icon="settings03">
                            Sources
                            <Badge hide={!selectedSources?.length}>{selectedSources?.length}</Badge>
                          </Button>
                        }
                        align="start"
                        side="top"
                        hideArrow
                        contentPadding={0}
                      >
                        <CustomerDocumentSelector
                          title={
                            <Text color="sysTextSecondary">Specify content library sources</Text>
                          }
                          inputPlaceholder="Search library"
                          documentType={CustomerDocumentTypeFilter.ContentLibrary}
                          control={control}
                          checkable
                          name="selectedSources"
                        />
                      </Popover>
                      <TovDropdown
                        selectedTovId={selectedTovId}
                        onSelect={handleTovSelect}
                        disabled={isLoading}
                      />
                    </Flex>

                    <Flex gap={8} alignItems="center">
                      {/* TODO: add attachment button */}
                      {/* <Button buttonStyle="inverse" icon="attachment01" /> */}
                      {formState.errors.userInputQuestion && (
                        <Flex alignItems="center" gap={8}>
                          <UIcon icon="infoCircle" size={16} color="sysTextSecondary" />
                          <Text color="sysTextSecondary" description>
                            {formState.errors.userInputQuestion.message}
                          </Text>
                        </Flex>
                      )}
                      <Button
                        buttonType="primary"
                        disabled={Boolean(formState.errors.userInputQuestion)}
                        type="submit"
                        aria-label="Ask question"
                        icon="arrowUp"
                        iconProps={{ size: 16 }}
                        style={{ borderRadius: 4 }}
                      />
                    </Flex>
                  </Flex>
                </Flex>
              </Card>
            );
          }}
          renderEmptyThread={({ methods, selectSuggestedQuestion }) => {
            return (
              <EmptyThreadPlaceholder>
                <EmptyState
                  type="startingState"
                  heading="Get started with your AI assistant"
                  description="Context is sourced from tender documents, Stotles data, your bid library, and the web"
                  action={
                    <QuickPromptsMenu
                      align="center"
                      prompts={suggestedQuestions}
                      unavailableReason={quickPromptsUnavailableReason}
                      trigger={
                        <QuickActionButton
                          text="Quick prompts"
                          icon="magicWand"
                          disabled={!quickPrompts}
                        />
                      }
                      onSelectPrompt={(quickPrompt) =>
                        void selectSuggestedQuestion(
                          quickPrompt.prompt,
                          quickPromptTracking(quickPrompt),
                        )
                      }
                      focusInput={() => focusInputAtEnd(methods)}
                      onEditPrompts={openQuickPromptsEditor}
                    />
                  }
                />
              </EmptyThreadPlaceholder>
            );
          }}
        />
      </AiContextProvider>
      <QuickPromptsDrawer
        chatType={AiQuickPromptChatType.BidResponse}
        open={isEditingQuickPrompts}
        onOpenChange={setIsEditingQuickPrompts}
      />
    </AiAssistantContainer>
  );
}

const ActionsContainer = styled(Flex)({
  marginLeft: 40,
  alignItems: "flex-start",
  gap: 12,
  minHeight: 32,
  justifyContent: "flex-start",
});

const SourcesContainer = styled.div({
  marginTop: 4,
});

const AiAssistantContainer = styled.div({
  height: "100%",
});

const EmptyThreadPlaceholder = styled(Flex)({
  height: "100%",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
});
